<template>
    <div v-if="yieldMap.isYieldLoading" class="yield-map-legend">
        <ProgressSpinner 
            style="width: 20px; height: 20px"
            strokeWidth="4"
            fill="#212121"
            animationDuration=".5s"
            aria-label="Custom ProgressSpinner"
        />
        <span class="yield-map-legend-loading-text">{{ $t('Checking for yield data...') }}</span>
    </div>
    <div v-else-if="legendRows.length && !yieldMap.isYieldLoading" class="yield-map-legend">
        <div class="yield-map-legend-header">
            <div class="yield-map-legend-subtitle-action">
                <strong>{{ yieldTypeLabel }}</strong>
                <Button
                    icon="pi pi-times"
                    severity="danger"
                    variant="text"
                    size="small"
                    @click="clearYieldMap()"
                    v-tooltip.bottom="$t('Clear yield map')"
                />
            </div>
            <div class="yield-map-legend-unit">
                <span>{{ $t('Unit') }}: {{ yieldUnit }}</span>
            </div>
        </div>
        <Divider class="m-0" />
        <div v-for="(row, index) in legendRows" :key="index" class="yield-map-legend-row">
            <span class="yield-map-legend-color" :style="{ backgroundColor: row.color }"></span>
            <span class="yield-map-legend-range">{{ row.label }}</span>
        </div>
        <Divider class="m-0" />
        <div class="yield-map-legend-subtitle-container">
            <span class="yield-map-legend-subtitle">{{ $t('DDI') }}: {{ yieldDDI }}</span>
            <span class="yield-map-legend-subtitle">{{ $t('Points') }}: {{ pointsCount }}</span>
        </div>
    </div>
</template>

<script>
import { useYieldMap } from '@/stores/yieldMap'
import { useTasksStore } from '@/stores/tasks'
import { createDraggable } from 'animejs'
import {
    getYieldBucketsFromPoints,
    getYieldLegendRowsFromBuckets
} from '@/helpers/yieldMapBuckets'
import { useRoute } from 'vue-router'

export default {
    name: 'YieldMapLegend',
    setup() {
        const yieldMap = useYieldMap()
        const ts = useTasksStore()
        const route = useRoute()

        return {
            yieldMap,
            ts,
            route,
        }
    },
    watch: {
        isVisible: {
            immediate: true,
            handler(visible) {
                if (visible) {
                    if (!this.yieldMap.legendDraggable) {
                        this.yieldMap.legendDraggable = createDraggable('.yield-map-legend-control', {
                            container: '#map',
                            snap: 56,
                            dragThreshold: { mouse: 6, touch: 10 },
                        })
                    } else {
                        this.yieldMap.legendDraggable.enable()
                        this.yieldMap.legendDraggable.refresh()
                    }
                } else if (this.yieldMap.legendDraggable) {
                    this.yieldMap.legendDraggable.disable()
                }
            },
        },
        pointsCount() {
            if (this.isVisible && this.yieldMap.legendDraggable) {
                this.yieldMap.legendDraggable.refresh()
            }
        },
        'route.name': {
            immediate: true,
            handler(toName, fromName) {
                if (this.yieldMap.yieldData && toName !== fromName) {
                    this.yieldMap.clearYieldHeatmap(true)
                }
            }
        },
    },
    computed: {
        isVisible() {
            return this.legendRows.length > 0 && !this.yieldMap.isYieldLoading
        },
        yieldTypeLabel() {
            const label = this.yieldMap.yieldData?.legend?.label
            if (label) {
                return this.$t(label)
            }
            return this.$t('Yield')
        },
        yieldDDI() {
            return this.yieldMap.yieldData?.legend?.ddi ? this.yieldMap.yieldData.legend.ddi : this.$t('unknown')
        },
        yieldUnit() {
            return this.yieldMap.yieldData?.legend?.unit || this.$t('unknown')
        },
        pointsCount() {
            return this.yieldMap.yieldData?.stats?.returned || 0
        },
        legendRows() {
            const points = this.yieldMap.yieldData?.points
            const buckets = getYieldBucketsFromPoints(points)
            if (!buckets) {
                return []
            }
            return getYieldLegendRowsFromBuckets(buckets, this.formatValue)
        },
    },
    methods: {
        formatValue(value) {
            return Number(value).toFixed(2)
        },
        clearYieldMap() {
            this.yieldMap.clearYieldHeatmap(true)
        },
        async uploadISOData() {
            const taskId = this.yieldMap.isoData.taskId
            const file = this.yieldMap.isoData.file

            if (taskId == null || taskId === '' || !file) {
                this.toast.add({
                    severity: 'error',
                    summary: this.$t('Error'),
                    detail: this.$t('No ISO Data to upload'),
                    life: 10000
                })
                return
            }

            let res = await this.ts.uploadISOData(taskId, file)
            if (!res?.ok) {
                this.toast.add({
                    severity: 'error',
                    summary: this.$t('Error'),
                    detail: this.$t('Failed to upload ISO Data'),
                    life: 10000
                })
            } else {
                this.toast.add({
                    severity: 'success',
                    summary: this.$t('Success'),
                    detail: this.$t('ISO Data Uploaded'),
                    life: 3000,
                })
                await this.ts.getFieldTasks(this.ts.field.id)
            }
        },
    },
}
</script>

<style scoped>
.yield-map-legend {
    display: flex;
    flex-direction: column;
    gap: 8px;
    background-color: #212121;
    color: #fff;
    min-width: 180px;
    padding: 10px;
    border-radius: 8px;
    border: 1px solid #313131;
}

.yield-map-legend-title {
    display: flex;
    align-items: baseline;
    justify-content: center;
    width: 100%;
    gap: 6px;
}

.yield-map-legend-header {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-bottom: 2px;
}

.yield-map-legend-subtitle-action {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.yield-map-legend-unit {
    font-size: 12px;
    color: #c9c9c9;
}

.yield-map-legend-subtitle {
    font-size: 12px;
    color: #c9c9c9;
    margin-right: 4px;
}

.yield-map-legend-subtitle-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.yield-map-legend-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
}

.yield-map-legend-color {
    width: 14px;
    height: 14px;
    border-radius: 3px;
    border: 1px solid #1a1a1a;
    flex-shrink: 0;
}

.yield-map-legend-range {
    font-size: 12px;
    color: #ebebeb;
}

.yield-map-legend-loading-text {
    text-align: center;
    font-size: 14px;
    font-weight: 500;
    color: #ebebeb;
}
</style>
